import { Component, OnInit, ViewChild } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { MatButtonModule } from "@angular/material/button"
import { MatIconModule } from "@angular/material/icon"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatSelectModule } from "@angular/material/select"
import { MatInputModule } from "@angular/material/input"
import { MatDatepickerModule, MatDateRangePicker } from "@angular/material/datepicker"
import { MatDialog, MatDialogModule } from "@angular/material/dialog"
import { provideNativeDateAdapter } from "@angular/material/core"
import { PageHeaderComponent } from "../../shared/page-header/page-header.component"
import { DataLoaderComponent } from "../../shared/data-loader/data-loader.component"
import { PermissionService } from "../../services/permission.service"
import { SnackbarService } from "../../services/snackbar.service"
import { formatCalendarDate, lastNDaysRange } from "../../utils/business-date"
import {
  AdvertiserAnalyticsUser,
  AppActivity,
  UserAnalyticsService,
  UserAnalyticsSummary,
  UserAnalyticsTrendPoint,
} from "../../services/user-analytics.service"
import { AdvertiserDetailDialogComponent } from "./advertiser-detail-dialog.component"

@Component({
  selector: "app-user-analytics",
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatDatepickerModule,
    MatDialogModule,
    PageHeaderComponent,
    DataLoaderComponent,
  ],
  templateUrl: "./user-analytics.component.html",
  styleUrl: "./user-analytics.component.css",
})
export class UserAnalyticsComponent implements OnInit {
  @ViewChild("rangePicker") rangePicker!: MatDateRangePicker<Date>

  loading = false
  loadingUsers = false
  loadingAppActivity = false
  summary: UserAnalyticsSummary | null = null
  trend: UserAnalyticsTrendPoint[] = []
  users: AdvertiserAnalyticsUser[] = []
  appActivity: AppActivity | null = null

  activeFilter = "30d"
  dateFrom = ""
  dateTo = ""
  rangeStartDate: Date | null = null
  rangeEndDate: Date | null = null
  accountTypeFilter = ""
  activityFilter = ""
  verificationFilter = ""
  searchQuery = ""

  currentPage = 1
  itemsPerPage = 10
  totalUsers = 0
  totalPages = 0

  readonly filterOptions = [
    { label: "Today", value: "today" },
    { label: "Last 3 days", value: "3d" },
    { label: "Last week", value: "7d" },
    { label: "Last 15 days", value: "15d" },
    { label: "Last month", value: "30d" },
    { label: "Last 3 months", value: "90d" },
    { label: "Last 6 months", value: "180d" },
    { label: "Annual", value: "365d" },
    { label: "Custom", value: "custom" },
  ]

  readonly accountTypeOptions = [
    { value: "", label: "All account types" },
    { value: "individual", label: "Individual" },
    { value: "business", label: "Business" },
  ]

  readonly activityOptions = [
    { value: "", label: "All activity" },
    { value: "ordered", label: "Ordered" },
    { value: "not_ordered", label: "Not ordered" },
  ]

  readonly verificationOptions = [
    { value: "", label: "All verification" },
    { value: "verified", label: "Verified" },
    { value: "pending", label: "Pending verification" },
  ]

  constructor(
    private analytics: UserAnalyticsService,
    private perms: PermissionService,
    private snackbar: SnackbarService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.computeDateRange(this.activeFilter)
    this.loadAll()
  }

  get signupChartHint(): string {
    switch (this.chartBucket()) {
      case "day":
        return "Each bar is one day"
      case "week":
        return "Each bar is one week"
      default:
        return "Each bar is one month"
    }
  }

  get signupChartMax(): number {
    if (!this.signupChartBars.length) return 0
    return Math.max(...this.signupChartBars.map((bar) => bar.value), 0)
  }

  get signupChartMid(): number {
    return Math.round(this.signupChartMax / 2)
  }

  get signupChartBars(): { label: string; hint: string; value: number; heightPct: number }[] {
    const points = this.bucketedTrend()
    if (!points.length) return []
    const max = Math.max(...points.map((point) => point.value), 1)
    return points.map((point) => ({
      ...point,
      heightPct: point.value > 0 ? Math.max((point.value / max) * 100, 8) : 0,
    }))
  }

  get activityBars(): { label: string; value: number; heightPct: number; accent: boolean }[] {
    if (!this.summary) return []
    const items = [
      { label: "Ordered", value: this.summary.ordered_count, accent: false },
      { label: "Not ordered", value: this.summary.not_ordered_count, accent: true },
    ]
    const max = Math.max(...items.map((i) => i.value), 1)
    return items.map((i) => ({ ...i, heightPct: (i.value / max) * 100 }))
  }

  get accountTypeBars(): { label: string; value: number; heightPct: number; accent: boolean }[] {
    if (!this.summary) return []
    const items = [
      { label: "Individual", value: this.summary.individual_count, accent: false },
      { label: "Business", value: this.summary.business_count, accent: true },
    ]
    const max = Math.max(...items.map((i) => i.value), 1)
    return items.map((i) => ({ ...i, heightPct: (i.value / max) * 100 }))
  }

  get hasAppActivity(): boolean {
    const activity = this.appActivity
    if (!activity?.available) return false
    return (
      activity.active_users_by_country.length > 0 ||
      activity.active_users_by_city.length > 0 ||
      activity.top_events.length > 0 ||
      activity.active_users_trend.length > 0
    )
  }

  get eventBarMax(): number {
    const events = this.appActivity?.top_events ?? []
    return Math.max(...events.map((event) => event.event_count), 1)
  }

  /** One point per ga4.active_users_trend row. Daily active users are not summed. */
  get activityTrendSeries(): { label: string; value: number }[] {
    const points = this.appActivity?.active_users_trend ?? []
    const showYear = this.selectedRangeDays() > 366
    return points
      .map((point) => {
        const date = this.parseTrendDate(point.date)
        if (!date) return null
        return {
          time: date.getTime(),
          label: date.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: showYear ? "2-digit" : undefined,
          }),
          value: point.active_users,
        }
      })
      .filter((point): point is { time: number; label: string; value: number } => point != null)
      .sort((a, b) => a.time - b.time)
      .map(({ label, value }) => ({ label, value }))
  }

  get activityLinePoints(): string {
    const series = this.activityTrendSeries
    if (!series.length) return ""
    const max = Math.max(...series.map((point) => point.value), 1)
    return series
      .map((point, index) => {
        const x = series.length === 1 ? 50 : (index / (series.length - 1)) * 100
        const y = 36 - (point.value / max) * 32
        return `${x.toFixed(2)},${y.toFixed(2)}`
      })
      .join(" ")
  }

  get activityLineLabels(): string[] {
    const series = this.activityTrendSeries
    if (series.length <= 6) return series.map((point) => point.label)
    const indexes = [0, Math.floor(series.length / 2), series.length - 1]
    return [...new Set(indexes)].map((index) => series[index].label)
  }

  eventBarWidth(count: number): number {
    if (!count) return 0
    return Math.max((count / this.eventBarMax) * 100, 4)
  }

  get paginationStart(): number {
    if (this.totalUsers === 0) return 0
    return (this.currentPage - 1) * this.itemsPerPage + 1
  }

  get paginationEnd(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalUsers)
  }

  onDateFilterChange(): void {
    this.setFilter(this.activeFilter)
  }

  setFilter(filter: string): void {
    this.activeFilter = filter
    if (filter !== "custom") {
      this.rangeStartDate = null
      this.rangeEndDate = null
      this.computeDateRange(filter)
      this.currentPage = 1
      this.loadAll()
    }
  }

  onCustomRangeChange(): void {
    if (this.activeFilter !== "custom") return
    if (this.rangeStartDate && this.rangeEndDate) {
      this.dateFrom = formatCalendarDate(this.rangeStartDate)
      this.dateTo = formatCalendarDate(this.rangeEndDate)
      this.currentPage = 1
      this.loadAll()
    }
  }

  openRangePicker(): void {
    this.rangePicker?.open()
  }

  clearCustomRange(event: Event): void {
    event.stopPropagation()
    this.rangeStartDate = null
    this.rangeEndDate = null
  }

  onFilterChange(): void {
    this.currentPage = 1
    this.loadUsers()
  }

  onSearch(query: string): void {
    this.searchQuery = query
    this.currentPage = 1
    this.loadUsers()
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return
    this.currentPage = page
    this.loadUsers()
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  getPaginationPages(): number[] {
    const pages: number[] = []
    const maxVisible = 5
    if (this.totalPages <= maxVisible) {
      for (let i = 1; i <= this.totalPages; i++) pages.push(i)
      return pages
    }
    const start = Math.max(1, this.currentPage - 2)
    const end = Math.min(this.totalPages, start + maxVisible - 1)
    for (let i = start; i <= end; i++) pages.push(i)
    return pages
  }

  displayName(user: AdvertiserAnalyticsUser): string {
    const name = `${user.firstname || ""} ${user.lastname || ""}`.trim()
    return name || "—"
  }

  initials(user: AdvertiserAnalyticsUser): string {
    const first = (user.firstname || "").charAt(0)
    const last = (user.lastname || "").charAt(0)
    return (first + last).toUpperCase() || "?"
  }

  accountTypeLabel(type: string): string {
    if (type === "business") return "Business"
    if (type === "individual") return "Individual"
    return type || "—"
  }

  /** Business accounts show business_name; individual accounts show signup_purpose. */
  businessOrPurpose(user: AdvertiserAnalyticsUser): string {
    const value = user.account_type === "business" ? user.business_name : user.signup_purpose
    const text = (value || "").trim()
    return text || "—"
  }

  formatSignupDate(value: string | null): string {
    if (!value) return "—"
    const iso = /^\d{4}-\d{2}-\d{2}/.exec(value)
    const d = iso ? new Date(iso[0] + "T12:00:00") : new Date(value)
    if (Number.isNaN(d.getTime())) return value
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
  }

  formatLastActivity(value: string | null): string {
    if (!value) return "Never logged in"
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return value
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
  }

  openUserDetail(user: AdvertiserAnalyticsUser): void {
    if (!user.user_id) return
    this.dialog.open(AdvertiserDetailDialogComponent, {
      width: "min(760px, 94vw)",
      maxWidth: "94vw",
      maxHeight: "90vh",
      panelClass: "app-advertiser-detail-dialog",
      autoFocus: false,
      restoreFocus: true,
      data: { userId: user.user_id },
    })
  }

  trackByUserId(_index: number, user: AdvertiserAnalyticsUser): number {
    return user.user_id
  }

  private dateFilters() {
    return {
      date_from: this.dateFrom,
      date_to: this.dateTo,
      granularity: this.trendGranularity(),
    }
  }

  private userFilters() {
    return {
      ...this.dateFilters(),
      account_type: this.accountTypeFilter || undefined,
      activity: this.activityFilter || undefined,
      verification: this.verificationFilter || undefined,
      search: this.searchQuery.trim() || undefined,
      limit: this.itemsPerPage,
      offset: (this.currentPage - 1) * this.itemsPerPage,
    }
  }

  private trendGranularity(): "day" | "week" {
    return this.chartBucket() === "month" ? "week" : "day"
  }

  private selectedRangeDays(): number {
    if (!this.dateFrom || !this.dateTo) return 30
    const from = this.parseTrendDate(this.dateFrom)
    const to = this.parseTrendDate(this.dateTo)
    if (!from || !to) return 30
    return Math.max(1, Math.round((to.getTime() - from.getTime()) / 86400000) + 1)
  }

  private chartBucket(): "day" | "week" | "month" {
    const days = this.selectedRangeDays()
    if (days <= 10) return "day"
    if (days <= 60) return "week"
    return "month"
  }

  private bucketedTrend(): { label: string; hint: string; value: number }[] {
    const bucket = this.chartBucket()
    const groups = new Map<string, { date: Date; value: number }>()

    for (const point of this.trend) {
      const date = this.parseTrendDate(point.label)
      if (!date) continue
      const keyDate =
        bucket === "month" ? new Date(date.getFullYear(), date.getMonth(), 1, 12) :
        bucket === "week" ? this.startOfWeek(date) :
        date
      const key = `${keyDate.getFullYear()}-${keyDate.getMonth()}-${keyDate.getDate()}`
      const existing = groups.get(key)
      if (existing) {
        existing.value += point.signups
      } else {
        groups.set(key, { date: keyDate, value: point.signups })
      }
    }

    const showYear = this.selectedRangeDays() > 366 || this.spansTwoYears(groups)
    return [...groups.values()]
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .map((group) => ({
        label: this.formatBucketLabel(group.date, bucket, showYear),
        hint: this.formatBucketHint(group.date, bucket, group.value),
        value: group.value,
      }))
  }

  private spansTwoYears(groups: Map<string, { date: Date; value: number }>): boolean {
    const years = new Set([...groups.values()].map((group) => group.date.getFullYear()))
    return years.size > 1
  }

  private startOfWeek(date: Date): Date {
    const copy = new Date(date)
    const day = copy.getDay()
    copy.setDate(copy.getDate() + (day === 0 ? -6 : 1 - day))
    copy.setHours(12, 0, 0, 0)
    return copy
  }

  private formatBucketLabel(date: Date, bucket: "day" | "week" | "month", showYear: boolean): string {
    if (bucket === "month") {
      return date.toLocaleDateString(undefined, {
        month: "short",
        year: showYear ? "2-digit" : undefined,
      })
    }
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
  }

  private formatBucketHint(date: Date, bucket: "day" | "week" | "month", value: number): string {
    const count = `${value} signup${value === 1 ? "" : "s"}`
    if (bucket === "month") {
      const month = date.toLocaleDateString(undefined, { month: "long", year: "numeric" })
      return `${month}: ${count}`
    }
    if (bucket === "week") {
      const end = new Date(date)
      end.setDate(date.getDate() + 6)
      const startLabel = date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
      const endLabel = end.toLocaleDateString(undefined, { month: "short", day: "numeric" })
      return `Week of ${startLabel}–${endLabel}: ${count}`
    }
    const day = date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })
    return `${day}: ${count}`
  }

  private parseTrendDate(label: string): Date | null {
    const iso = /^\d{4}-\d{2}-\d{2}/.exec(label)
    if (iso) {
      const date = new Date(`${iso[0]}T12:00:00`)
      return Number.isNaN(date.getTime()) ? null : date
    }
    const date = new Date(label)
    return Number.isNaN(date.getTime()) ? null : date
  }

  private loadAll(): void {
    if (!this.perms.canViewUserAnalytics()) return
    this.loading = true
    this.loadingUsers = true
    this.loadAppActivity()
    this.analytics.getAnalytics(this.userFilters()).subscribe({
      next: (res) => {
        this.summary = res.summary
        this.trend = res.trend
        this.users = res.users
        this.totalUsers = res.pagination.total
        this.totalPages = Math.ceil(this.totalUsers / this.itemsPerPage) || 0
        this.loading = false
        this.loadingUsers = false
      },
      error: (err) => {
        this.loading = false
        this.loadingUsers = false
        this.summary = null
        this.trend = []
        this.users = []
        this.totalUsers = 0
        this.totalPages = 0
        this.snackbar.error(this.snackbar.apiError(err, "Failed to load user analytics."))
      },
    })
  }

  private loadAppActivity(): void {
    if (!this.perms.canViewUserAnalytics()) return
    if (!this.dateFrom || !this.dateTo) {
      this.appActivity = null
      this.loadingAppActivity = false
      return
    }
    this.loadingAppActivity = true
    this.analytics.getAppActivity({ date_from: this.dateFrom, date_to: this.dateTo }).subscribe({
      next: (activity) => {
        this.appActivity = activity
        this.loadingAppActivity = false
      },
      error: () => {
        this.appActivity = null
        this.loadingAppActivity = false
      },
    })
  }

  private loadUsers(): void {
    if (!this.perms.canViewUserAnalytics()) return
    this.loadingUsers = true
    this.analytics.getUsers(this.userFilters()).subscribe({
      next: (res) => {
        this.users = res.users
        this.totalUsers = res.pagination.total
        this.totalPages = Math.ceil(this.totalUsers / this.itemsPerPage) || 0
        this.loadingUsers = false
      },
      error: (err) => {
        this.loadingUsers = false
        this.users = []
        this.totalUsers = 0
        this.totalPages = 0
        this.snackbar.error(this.snackbar.apiError(err, "Failed to load advertisers."))
      },
    })
  }

  private computeDateRange(filter: string): void {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    if (filter === "custom") {
      if (this.rangeStartDate && this.rangeEndDate) {
        this.dateFrom = formatCalendarDate(this.rangeStartDate)
        this.dateTo = formatCalendarDate(this.rangeEndDate)
      }
      return
    }

    // "Last N days" = exactly N calendar days including today (date_from/date_to are
    // inclusive in get_user_analytics_*), so from = today - (N - 1). ("today" is N = 1.)
    const days = Number.parseInt(filter, 10)
    const range = lastNDaysRange(filter.endsWith("d") && Number.isFinite(days) ? days : 1, today)
    this.dateFrom = range.from
    this.dateTo = range.to
  }

}
